// RigSentinel Calc — Current Well Status snapshot aggregator.
//
// Pure read-only aggregator that pulls the most-recent record from each of
// the six configured data sources and returns a normalized snapshot. No
// localStorage writes anywhere in this module.
//
//   1. Daily Drilling Report (DDR)
//   2. Daily Mud Report (DMR)
//   3. Surface or Subsea BOP Test Report (whichever is more recent)
//   4. Bit Run Record
//   5. Well Control — saved MAASP / MAASP Worksheet calculator sheet
//   6. Pump Output — saved Pump Output calculator sheet
//
// Each section in the returned snapshot conforms to the row shape:
//   {
//     label,            // human label e.g. "Daily Drilling Report"
//     value,            // primary field display string (or null if missing)
//     extras,           // optional { key: displayString }
//     asOf,             // ISO string of the source draft's reportDate / createdAt
//     sourceDraftId,    // ID of the source draft (or saved sheet) — for audit
//     missing,          // true if no source record was found at all
//     stale,            // true if the most-recent record is older than the
//                       // configured staleness threshold
//     warningText,      // present when missing or stale, banned-verdict clean
//   }
//
// Staleness thresholds are exported constants so callers can audit them.
//
// IMPORTANT: this module must never throw on malformed localStorage. Each
// fixture-shape access is defensive.

import {
  dmrStore,
  bitStore,
  sfbopStore,
  sbopStore,
} from "./storage";
import { getDdrDrafts } from "../ddr/storage";
import { getSaved } from "../storage";

// ---------------------------------------------------------------------------
// Staleness thresholds (days)
// ---------------------------------------------------------------------------
export const STALENESS_DAYS = Object.freeze({
  ddr: 2,
  mud: 2,
  bop: 30,
  bit: 30,
  wellControl: 7,
  pump: 7,
});

const _DAY_MS = 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const _ts = (v) => {
  if (!v) return 0;
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : 0;
};

const _mostRecentTs = (rec) =>
  Math.max(
    _ts(rec?.updatedAt),
    _ts(rec?.createdAt),
    _ts(rec?.header?.reportDate),
    _ts(rec?.createdAtIso),
    0,
  );

function _pickMostRecent(list) {
  if (!Array.isArray(list) || list.length === 0) return null;
  let best = null;
  let bestTs = -1;
  for (const r of list) {
    const t = _mostRecentTs(r);
    if (t > bestTs) {
      best = r;
      bestTs = t;
    }
  }
  return best;
}

function _isStale(asOfMs, thresholdDays, nowMs = Date.now()) {
  if (!asOfMs) return false;
  return nowMs - asOfMs > thresholdDays * _DAY_MS;
}

function _isoOrNull(ms) {
  if (!ms) return null;
  try {
    return new Date(ms).toISOString().slice(0, 10);
  } catch {
    return null;
  }
}

function _firstNonEmpty(...vals) {
  for (const v of vals) {
    if (v === 0 || v === false) return v;
    if (v != null && v !== "") return v;
  }
  return null;
}

function _maxNumber(...vals) {
  let best = null;
  for (const v of vals) {
    const n = parseFloat(v);
    if (Number.isFinite(n) && (best == null || n > best)) best = n;
  }
  return best;
}

// ---------------------------------------------------------------------------
// Per-source extractors. Each returns the canonical row shape.
// ---------------------------------------------------------------------------
function _row({ label, draft, fields, threshold, missingMsg, staleMsg, nowMs }) {
  if (!draft) {
    return {
      label,
      value: null,
      extras: {},
      asOf: null,
      sourceDraftId: null,
      missing: true,
      stale: false,
      warningText: missingMsg,
    };
  }
  const asOfMs = _mostRecentTs(draft);
  const stale = _isStale(asOfMs, threshold, nowMs);
  return {
    label,
    value: fields.value ?? null,
    extras: fields.extras || {},
    asOf: _isoOrNull(asOfMs),
    sourceDraftId: draft.id || null,
    missing: false,
    stale,
    warningText: stale ? staleMsg : null,
  };
}

function _extractDdr(draft) {
  if (!draft) return {};
  const params = draft.params || {};
  const ops = draft.ops || {};
  const depth = _firstNonEmpty(params.holeDepth, params.bitDepth);
  const operation = _firstNonEmpty(ops.mainOperation, ops.currentStatus);
  return {
    value: depth != null ? `${depth} ft` : null,
    extras: {
      Operation: operation || "—",
      "Report date": draft.header?.reportDate || "—",
    },
  };
}

function _extractMud(draft) {
  if (!draft) return {};
  const mud = draft.mud || {};
  const volumes = draft.volumes || {};
  const mw = _firstNonEmpty(mud.mudWeight);
  const pit = _firstNonEmpty(volumes.activePit);
  const solids = _firstNonEmpty(mud.solids);
  return {
    value: mw != null ? `${mw} ppg` : null,
    extras: {
      "Active pit": pit != null ? `${pit} bbl` : "—",
      "Solids %": solids != null ? `${solids}%` : "—",
      "Report date": draft.header?.reportDate || "—",
    },
  };
}

function _bopMaxPressure(draft) {
  if (!draft) return null;
  return _maxNumber(
    draft.annular?.highP,
    draft.annular?.lowP,
    draft.ram?.highP,
    draft.ram?.lowP,
    draft.choke?.highP,
    draft.kill?.highP,
  );
}

function _extractBop(surfaceDraft, subseaDraft) {
  const sTs = _mostRecentTs(surfaceDraft);
  const ssTs = _mostRecentTs(subseaDraft);
  const draft = ssTs > sTs ? subseaDraft : surfaceDraft;
  const kind = ssTs > sTs ? "Subsea" : "Surface";
  if (!draft) return { value: null, extras: {}, draft: null };
  const maxP = _bopMaxPressure(draft);
  return {
    value: draft.header?.reportDate || null,
    extras: {
      "Stack type": kind,
      "Max test pressure": maxP != null ? `${maxP} psi` : "—",
    },
    draft,
  };
}

function _extractBit(draft) {
  if (!draft) return {};
  const run = draft.run || {};
  const pull = draft.pull || {};
  const hours = _firstNonEmpty(run.bitHours);
  const footage = _firstNonEmpty(run.intervalDrilled);
  const dull = _firstNonEmpty(pull.dullGrade);
  return {
    value: hours != null ? `${hours} hr on bit` : null,
    extras: {
      Footage: footage != null ? `${footage} ft` : "—",
      "Dull grade": dull || "—",
      "Report date": draft.header?.reportDate || "—",
    },
  };
}

function _extractWellControl(sheet) {
  if (!sheet) return {};
  const out = _firstNonEmpty(sheet.displayResult, sheet.result);
  const unit = sheet.displayUnit || sheet.unit || "psi";
  const mw = _firstNonEmpty(
    sheet.inputs?.currentMudWeight,
    sheet.inputs?.mudWeight,
    sheet.inputs?.mw,
  );
  return {
    value: out != null ? `${out} ${unit}` : null,
    extras: {
      "Mud weight (entered)": mw != null ? `${mw} ppg` : "—",
      "Sheet": sheet.calcName || sheet.calcId || "MAASP",
    },
  };
}

function _extractPump(sheet) {
  if (!sheet) return {};
  const out = _firstNonEmpty(sheet.displayResult, sheet.result);
  const unit = sheet.displayUnit || sheet.unit || "bbl/stk";
  const spm = _firstNonEmpty(sheet.inputs?.spm, sheet.inputs?.strokesPerMinute);
  const pressure = _firstNonEmpty(sheet.inputs?.pressure, sheet.inputs?.standpipePressure);
  return {
    value: out != null ? `${out} ${unit}` : null,
    extras: {
      "SPM": spm != null ? String(spm) : "—",
      "Pressure (psi)": pressure != null ? String(pressure) : "—",
    },
  };
}

// ---------------------------------------------------------------------------
// Public aggregator
// ---------------------------------------------------------------------------
/**
 * Compute the current well status snapshot.
 *
 * @param {object} opts
 * @param {number} [opts.nowMs] — override for "now" timestamp (used by tests
 *   to assert stale detection deterministically).
 * @returns snapshot object — see module header for the shape.
 */
export function getWellStatusSnapshot(opts = {}) {
  const nowMs = opts.nowMs || Date.now();

  // --- 1. DDR -------------------------------------------------------------
  const ddrDraft = _pickMostRecent(getDdrDrafts());
  const ddr = _row({
    label: "Daily Drilling Report",
    draft: ddrDraft,
    fields: _extractDdr(ddrDraft),
    threshold: STALENESS_DAYS.ddr,
    missingMsg: "⚠️ No Daily Drilling Report found. Please verify before tour.",
    staleMsg: "⚠️ Daily Drilling Report not updated in 48 hours.",
    nowMs,
  });

  // --- 2. Mud -------------------------------------------------------------
  const mudDraft = _pickMostRecent(dmrStore.getAll());
  const mud = _row({
    label: "Daily Mud Report",
    draft: mudDraft,
    fields: _extractMud(mudDraft),
    threshold: STALENESS_DAYS.mud,
    missingMsg: "⚠️ No Daily Mud Report found. Please verify before tour.",
    staleMsg: "⚠️ Daily Mud Report not updated in 48 hours.",
    nowMs,
  });

  // --- 3. BOP (surface OR subsea, whichever is more recent) ---------------
  const sf = _pickMostRecent(sfbopStore.getAll());
  const ss = _pickMostRecent(sbopStore.getAll());
  const bopExtract = _extractBop(sf, ss);
  const bopDraft = bopExtract.draft;
  const bop = _row({
    label: "BOP Test Report",
    draft: bopDraft,
    fields: bopDraft
      ? { value: bopExtract.value, extras: bopExtract.extras }
      : {},
    threshold: STALENESS_DAYS.bop,
    missingMsg: "⚠️ No BOP test record found in last 30 days. Please verify.",
    staleMsg: "⚠️ No BOP test record found in last 30 days. Please verify.",
    nowMs,
  });

  // --- 4. Bit Run ---------------------------------------------------------
  const bitDraft = _pickMostRecent(bitStore.getAll());
  const bit = _row({
    label: "Bit Run Record",
    draft: bitDraft,
    fields: _extractBit(bitDraft),
    threshold: STALENESS_DAYS.bit,
    missingMsg: "⚠️ No bit run record found in last 30 days.",
    staleMsg: "⚠️ No bit run record found in last 30 days.",
    nowMs,
  });

  // --- 5. Well Control (MAASP) -------------------------------------------
  const saved = getSaved();
  const maaspSheet = _pickMostRecent(
    saved.filter((s) => s.calcId === "maasp_worksheet" || s.calcId === "maasp"),
  );
  const wellControl = _row({
    label: "Well Control · MAASP",
    draft: maaspSheet,
    fields: _extractWellControl(maaspSheet),
    threshold: STALENESS_DAYS.wellControl,
    missingMsg: "⚠️ No MAASP calculation found. Recalculate before tour.",
    staleMsg: "⚠️ MAASP not refreshed in last 7 days. Recalculate before tour.",
    nowMs,
  });

  // --- 6. Pump Output ----------------------------------------------------
  const pumpSheet = _pickMostRecent(saved.filter((s) => s.calcId === "pump_output"));
  const pump = _row({
    label: "Pump Output",
    draft: pumpSheet,
    fields: _extractPump(pumpSheet),
    threshold: STALENESS_DAYS.pump,
    missingMsg: "⚠️ No pump output calculation found.",
    staleMsg: "⚠️ Pump output (SPM / pressure) not refreshed in last 7 days.",
    nowMs,
  });

  const rows = { ddr, mud, bop, bit, wellControl, pump };
  const warnings = Object.values(rows)
    .filter((r) => r.warningText)
    .map((r) => r.warningText);

  return {
    generatedAt: new Date(nowMs).toISOString(),
    rows,
    warnings,
  };
}

/**
 * Format the snapshot as a multi-line plain-text block suitable for both
 * pasting into the Operations Snapshot textarea AND emitting from
 * `buildHandoverNotes()` into the PDF. Banned-verdict-clean wording.
 */
export function formatSnapshotForText(snapshot) {
  if (!snapshot || !snapshot.rows) return "";
  const lines = [];
  const order = ["ddr", "mud", "bop", "bit", "wellControl", "pump"];
  for (const key of order) {
    const r = snapshot.rows[key];
    if (!r) continue;
    const head = `${r.label}: ${r.value != null ? r.value : "—"}`;
    lines.push(head);
    if (r.asOf) lines.push(`  As of: ${r.asOf}`);
    if (r.extras) {
      for (const [k, v] of Object.entries(r.extras)) {
        if (v == null || v === "" || v === "—") continue;
        lines.push(`  ${k}: ${v}`);
      }
    }
    if (r.warningText) lines.push(`  ${r.warningText}`);
    lines.push("");
  }
  return lines.join("\n").trim();
}
